const TERMINAL_FAIL_STATUSES = ['failed', 'canceled', 'skipped'];

const stageState = (statusValue, autoDismissedAt) => {
  if (statusValue === 'success') return 'done';
  if (TERMINAL_FAIL_STATUSES.includes(statusValue)) return 'failed';
  if (autoDismissedAt) return 'stale';
  return 'active';
};

// Derives the 3-step pipeline stepper (trigger sent -> submodules updated ->
// build/deploy) shown on the all-notifications page from a shaped
// notification. Returns null for local/ephemeral notifications, which carry
// no orchestrationStatus - there's no pipeline run behind them to chart.
const buildTimelineSteps = (notification) => {
  if (!notification.orchestrationStatus) return null;

  const steps = [{ key: 'triggered', state: 'done' }];

  const updatingState = stageState(notification.orchestrationStatus, notification.autoDismissedAt);
  steps.push({
    key: 'updating',
    state: updatingState,
    rawStatus: notification.orchestrationStatus,
    // detail is only ever set when the orchestration trigger call itself
    // threw, before a pipeline even existed - it never describes the
    // moodle/deploying side, so it's only attached here.
    detailText: notification.detail || null,
  });

  // The moodle pipeline is only triggered once the submodule-update commit
  // lands, i.e. once orchestration actually succeeds - anything else means
  // step 3 either hasn't started yet (active/stale) or never will (failed).
  if (updatingState !== 'done') {
    steps.push({ key: 'deploying', state: updatingState === 'failed' ? 'skipped' : 'pending' });
    return steps;
  }

  if (notification.deploySegments) {
    steps.push({ key: 'deploying', state: 'segments', segments: notification.deploySegments });
    return steps;
  }

  if (!notification.moodleStatus) {
    steps.push({ key: 'deploying', state: 'pending' });
    return steps;
  }

  steps.push({
    key: 'deploying',
    state: stageState(notification.moodleStatus, notification.autoDismissedAt),
    rawStatus: notification.moodleStatus,
  });
  return steps;
};

// Collapses a 'segments' deploying step (per-environment chips) into a
// single done/active/failed state, for surfaces that fold the step down to
// one line instead of showing a chip per environment.
const collapseSegmentsState = (segments) => {
  const statusKeys = segments.map((segment) => segment.statusKey);
  if (statusKeys.includes('failed')) return 'failed';
  if (statusKeys.some((key) => key === 'manual' || key === 'running')) return 'active';
  return 'done';
};

// Strips the raw GitLab statuses, free-text detail, and per-environment job
// names (job names are internal env codenames like "updatetest") down to
// just {key, state} - the shape NotificationTimeline already renders with
// no subtext/chips for states like "pending" today, so nothing there needs
// to change for customers to see a jargon-free stepper (SPECTR-258).
const simplifyTimelineSteps = (steps) => steps.map((step) => (
  step.state === 'segments'
    ? { key: step.key, state: collapseSegmentsState(step.segments) }
    : { key: step.key, state: step.state }
));

// Only for steps that have actually started - a step that's pending or
// skipped has nothing real to report yet, so no word is folded into it.
const WORDED_STATES = ['done', 'active', 'failed', 'stale'];

// The collapsed state a step should be rendered/colored as - 'segments'
// (per-environment chips) isn't itself a renderable state, it's reduced to
// whichever of done/active/failed best describes the group as a whole.
const stepDisplayState = (step) => (
  step.state === 'segments' ? collapseSegmentsState(step.segments) : step.state
);

// The folded "<Step label>: <word>" text (or null). `translate` maps a raw
// status word (e.g. 'running', 'manual') to its displayed form - callers
// own the actual i18n lookup since that needs useI18n()'s `t`.
const stepWord = (step, translate) => {
  if (step.detailText) return step.detailText;

  if (step.state === 'segments') {
    const collapsed = collapseSegmentsState(step.segments);
    if (!WORDED_STATES.includes(collapsed)) return null;
    const hasManual = step.segments.some((segment) => segment.statusKey === 'manual');
    if (hasManual) return translate('manual');
    if (collapsed === 'failed') return translate('failed');
    return translate(collapsed === 'done' ? 'success' : 'running');
  }

  if (!WORDED_STATES.includes(step.state) || !step.rawStatus) return null;
  return translate(step.rawStatus);
};

export { buildTimelineSteps, collapseSegmentsState, simplifyTimelineSteps, stepDisplayState, stepWord };
